/**
 * Показ страйка, индекса, отклонения и оценки исхода по модели.
 *
 * С 2026-08-27 «модель» на панели — это ЗАМОК исхода (`1 − prob_flip`), а не
 * Блэк — Шоулз: у замка откалиброван хвост и условен разброс остатка, и
 * расходились они с прежним числом втрое-стократно в рабочей области. Прежнее
 * число приезжает рядом полем `price_normal` и живёт в подсказке.
 *
 * Чистый модуль без React: логика живёт в lib/*.ts, потому что vitest в этом
 * проекте включает только `*.test.ts` и компонентных тестов нет вовсе. Компонент
 * (components/IndexRow.tsx) остаётся разметкой над этими функциями.
 *
 * Считать здесь нечего: модель посчитана в Go и приезжает готовой. Одна
 * реализация на систему — иначе TypeScript и Go разойдутся на первой же правке,
 * а расхождение видно только по деньгам.
 */

import type { FairPrice, FeeSchedule, Valuation } from "../api/types";
import { feePerShare } from "./fees";
import { add, format, parse, roundToFixed, SCALE, sub, type Micro } from "./money";

/** Чего не хватает, если оценки нет. Пустая строка — оценка есть. */
export type Tone = "up" | "down" | "flat";

/**
 * Сколько знаков показывать у цены индекса.
 *
 * Зависит от масштаба, а не от актива: BTC за 65 000 читают до центов, а DOGE за
 * 0.0696 без шестого знака превращается в «0.07» — то есть в число, по которому
 * отклонение не увидеть вовсе.
 */
export function indexDecimals(m: Micro): number {
  const abs = Math.abs(m);
  if (abs >= 100 * 1_000_000) return 2;
  if (abs >= 1_000_000) return 4;
  return 6;
}

/**
 * Цена индекса с разделением разрядов: «65 018.90».
 *
 * Разряды разделены неразрывным пробелом: «65018.90» на пятизначном числе
 * читается заметно хуже, а именно его оператор сравнивает со страйком глазами.
 * Разделитель дробной части — точка, как во всём остальном терминале.
 *
 * Лишние знаки ОКРУГЛЯЮТСЯ, а не отбрасываются, и это расхождение с лесенкой цен
 * сознательное (money.roundToFixed). Цену ордера усечение защищает: она не должна
 * расти от форматирования. Индекс и страйк никуда не подставляются, зато их
 * сравнивают глазами с Price To Beat и Current Price у Polymarket, а тот
 * округляет, — и усечение давало здесь систематический сдвиг до копейки вниз
 * поверх настоящего расхождения, мешая его разбирать.
 */
export function formatIndex(price: string | null | undefined): string {
  const m = parse(price);
  if (m === null) return "—";
  return groupThousands(roundToFixed(m, indexDecimals(m)));
}

/**
 * Куда ушёл индекс относительно страйка — СЛОВОМ.
 *
 * Слово, а не знак: «выше» и «ниже» — это и есть вопрос, на который смотрят на
 * этом рынке, и читаются они раньше цифр. Знак «+» приходится сопоставлять с
 * тем, что считается от чего, а на пятиминутном окне такого времени нет.
 *
 * Ровно на страйке — «на страйке», а не «выше на 0»: рынок в этот момент
 * резолвится в «Up», и говорить о направлении нечего.
 */
export function deviationLabel(deviation: string | null | undefined): string {
  const m = parse(deviation);
  if (m === null) return "";
  if (m === 0) return "на страйке";
  return m > 0 ? "выше на" : "ниже на";
}

/**
 * Насколько ушёл индекс — БЕЗ знака: «33.19».
 *
 * Знака здесь нет намеренно: направление уже сказано словом (deviationLabel), и
 * «ниже на −33.19» — это двойное отрицание, которое читается как ошибка.
 *
 * Знаки после запятой берутся от МАСШТАБА ИНДЕКСА, а не от величины самого
 * отклонения. Иначе отклонение в 33 доллара на BTC показывалось бы как «33.1900»
 * (четыре знака, потому что 33 меньше сотни), а на DOGE отклонение в 0.0001
 * схлопнулось бы в ноль. Мерять отклонение надо той же линейкой, что и то, от
 * чего оно отсчитано.
 */
export function formatDeviationMagnitude(
  deviation: string | null | undefined,
  reference: string | null | undefined,
): string {
  const m = parse(deviation);
  if (m === null) return "—";
  const ref = parse(reference);
  return groupThousands(roundToFixed(Math.abs(m), indexDecimals(ref ?? m)));
}

/**
 * Отклонение в процентах, тоже без знака: «0.05 %».
 *
 * Два знака: на пятиминутном окне BTC проходит порядка сотых долей процента, и
 * один знак округлял бы весь рабочий диапазон в «0.1 %».
 */
export function formatDeviationPct(pct: number | null | undefined): string {
  if (typeof pct !== "number" || !Number.isFinite(pct)) return "—";
  return `${Math.abs(pct).toFixed(2)} %`;
}

/**
 * Есть ли что показать про отклонение.
 *
 * Отдельный вопрос от «есть ли оценка модели», и это главное здесь: отклонение
 * считается из страйка и индекса, а модели нужна ещё и волатильность. Пока σ
 * набирается (первые минуты после перезапуска трейдера), оценки нет — но «выше
 * или ниже и насколько» известно точно, и прятать это вместе с моделью значит
 * убрать с экрана ровно то, ради чего строка заведена.
 */
export function hasDeviation(v: Valuation | undefined): boolean {
  return Boolean(v?.strike && v?.index && v?.deviation !== undefined);
}

/** Куда смотрит отклонение — от этого зависит цвет. */
export function deviationTone(deviation: string | null | undefined): Tone {
  const m = parse(deviation);
  if (m === null || m === 0) return "flat";
  return m > 0 ? "up" : "down";
}

/**
 * Справедливая цена исхода: «0.632».
 *
 * Три знака — максимальная точность шага цены на этих рынках (tick_size
 * доходит до 0.001). Округлять до шага КОНКРЕТНОГО рынка здесь нельзя: оценка
 * модели не ордер, и подгонка её под сетку цен выглядела бы как готовая заявка.
 */
export function formatFair(price: string | null | undefined): string {
  const m = parse(price);
  if (m === null) return "—";
  // Округление, а не усечение: цена модели справочная и никуда не подставляется,
  // а на трёх знаках усечение сдвигало бы её вниз на весь рабочий диапазон.
  return roundToFixed(m, 3);
}

/**
 * Волатильность как «42 %».
 *
 * Годовая, как её и меряет модель. Целые проценты: разница между 42 % и 42.3 %
 * не меняет ни одного решения оператора, а лишний знак занимает место в строке,
 * где всё остальное — деньги.
 */
export function formatSigma(sigma: number | null | undefined): string {
  if (typeof sigma !== "number" || !Number.isFinite(sigma) || sigma <= 0) return "—";
  return `${Math.round(sigma * 100)} %`;
}

/**
 * Полоса безубытка тейкера вокруг справедливой цены: «0.615 … 0.649».
 *
 * # Зачем
 *
 * Оператор сравнивает цену модели с ценой стакана и решает, стоит ли переходить
 * спред. Комиссия сдвигает этот порог, и заметно: у p ≈ 0.5 она равна 1.75 ¢ на
 * share, то есть почти два тика. Показывать справедливую цену без порога значило
 * бы предлагать сделку, которая по модели уже убыточна.
 *
 * # Что именно считается
 *
 * Покупать тейкером имеет смысл дешевле, чем `fair − комиссия/share`, продавать —
 * дороже, чем `fair + комиссия/share`. Комиссия берётся В ТОЧКЕ справедливой
 * цены: строго порог решает уравнение `p + rate·p·(1−p) = fair`, и первый шаг его
 * решения — то, что здесь и посчитано. Разница между приближением и корнем на
 * рабочих ценах меньше двух десятитысячных, но назвать приближение приближением
 * честнее, чем изображать точность: подпись в интерфейсе так и говорит.
 *
 * null — комиссии нет, расписание рынка неизвестно или цены модели нет. Прочерк
 * в этом случае лучше числа: порог, посчитанный по выдуманной ставке, хуже
 * отсутствующего.
 */
export function takerBand(
  fair: string | null | undefined,
  schedule: FeeSchedule | undefined,
): { buyBelow: string; sellAbove: string; perShare: string } | null {
  const p = parse(fair);
  if (p === null || p <= 0 || p >= SCALE) return null;
  const fee = feePerShare(schedule, p);
  if (fee === null || fee <= 0) return null;
  const low = sub(p, fee);
  const high = add(p, fee);
  if (low === null || high === null || low <= 0 || high >= SCALE) return null;
  return {
    buyBelow: roundToFixed(low, 3),
    sellAbove: roundToFixed(high, 3),
    perShare: roundToFixed(fee, 4),
  };
}

/**
 * Справедливые цены в порядке outcome_index.
 *
 * Порядок значим ровно так же, как на странице рынка: лесенки и панель быстрых
 * действий выстроены по нему, и строка индекса обязана читаться слева направо в
 * том же порядке, иначе «Up 0.63» окажется над лесенкой Down.
 */
export function fairByIndex(v: Valuation | undefined): FairPrice[] {
  if (!v?.fair) return [];
  return [...v.fair].sort((a, b) => a.outcome_index - b.outcome_index);
}

/**
 * Что показать вместо чисел, когда оценки нет.
 *
 * Причина приходит с сервера словами и по-русски: она отвечает на вопрос «почему
 * пусто», а пустое место оператор прочитает как «отклонение нулевое». Здесь
 * только запасной вариант на случай молчащего сервера.
 */
export function unknownReason(v: Valuation | undefined): string {
  if (!v) return "оценка не загружена";
  return v.reason || "оценка недоступна";
}

/**
 * Возраст значения индекса в миллисекундах. NaN, если метки нет.
 *
 * Считается от метки ИСТОЧНИКА, а не от момента ответа: трейдер переписывает
 * ключ раз в секунду даже при молчащем потоке, поэтому свежесть ответа ничего не
 * говорит о свежести цены.
 */
export function indexAge(v: Valuation | undefined, now: number): number {
  if (!v?.index_at) return NaN;
  const t = new Date(v.index_at).getTime();
  return Number.isNaN(t) ? NaN : now - t;
}

/**
 * Метрика резолва коротким именем: «TWAP-30».
 *
 * Короткое имя, а не топик целиком, потому что читают здесь ровно одно — окно
 * усреднения. `crypto_prices_twap_thirty` и `crypto_prices_twap_sixty`
 * отличаются последним словом и на строке шириной в экран сливаются, а вопрос
 * «той ли метрикой посчитано» — единственный, ради которого подпись стоит.
 *
 * Окно берётся из ОТВЕТА, где оно взято из самого рынка, а не выводится из имени
 * топика: разойдись они, увидеть это надо здесь, а не в логах.
 */
export function metricLabel(v: Valuation | undefined): string {
  if (!v) return "—";
  if (typeof v.window_s === "number" && v.window_s > 0) return `TWAP-${v.window_s}`;
  return v.source || "—";
}

/** Подсказка к метрике: полное имя топика и почему это важно. */
export function sourceTitle(v: Valuation | undefined): string {
  const window =
    typeof v?.window_s === "number" && v.window_s > 0
      ? `рынок резолвится по ${v.window_s}-секундному TWAP Chainlink`
      : "окно усреднения рынка неизвестно";
  const topic = v?.source ? `; поток: ${v.source}` : "";
  return (
    `${window}${topic}. Метрика взята из самого рынка ` +
    "(cryptoMarketConfig.twapLookbackSeconds): соседние метрики того же потока " +
    "расходятся с нужной на 0.03 доллара по ETH и на 3.73 по BTC"
  );
}

/**
 * Опоздал ли страйк относительно начала окна.
 *
 * Порог в одну секунду, а не ноль: поток идёт раз в секунду, метки выровнены по
 * целой секунде, и опоздание меньше секунды означает ровно «то самое значение».
 * Отсутствие метки (страйк записан до миграции 00007) — не опоздание, а незнание,
 * и врать про него нулём нельзя.
 */
export function hasStrikeLag(v: Valuation | undefined): boolean {
  return typeof v?.strike_lag_ms === "number" && v.strike_lag_ms >= 1000;
}

/**
 * На сколько опоздало значение, взятое за страйк: «взят на 2 с позже начала».
 *
 * Словами, а не числом миллисекунд: это предупреждение, а не измерение. Означает
 * оно, что секунду начала окна источник пропустил, и страйк смещён на движение
 * цены за эти секунды — правдоподобно и в одну сторону.
 */
export function strikeLagLabel(v: Valuation | undefined): string {
  if (!hasStrikeLag(v)) return "";
  const seconds = Math.round((v?.strike_lag_ms ?? 0) / 1000);
  return `взят на ${seconds} с позже начала`;
}

/** Подсказка к страйку: чем и когда измерен. */
export function strikeTitle(v: Valuation | undefined): string {
  if (!v?.strike) return "страйк окна не записан";
  const metric = metricLabel(v);
  if (!v.strike_sample_at) {
    // Страйк записан до миграции 00007: метки использованного значения нет, и
    // выдавать это за нулевое опоздание нельзя.
    return `цена на начало окна, метрика ${metric}; метка значения не сохранена`;
  }
  const lag = v.strike_lag_ms ?? 0;
  const when =
    lag >= 1000
      ? `значение опоздало на ${Math.round(lag / 1000)} с: секунду начала окна источник пропустил`
      : "измерено ровно на секунду начала окна";
  return `цена на начало окна, метрика ${metric}; ${when}`;
}

/** Неразрывный пробел между разрядами целой части. */
function groupThousands(s: string): string {
  const [int = "", frac] = s.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return frac ? `${grouped}.${frac}` : grouped;
}

/**
 * Середина стакана по лучшим уровням. null — считать нечем.
 *
 * Нужна затем, что цена модели сама по себе решения не даёт: она отвечает
 * «сколько исход стоит», а торгуют РАЗНИЦУ между этим и тем, что просит рынок.
 * Одностороннего стакана достаточно, чтобы середины не было: половина спреда —
 * это не середина, а другая величина, и подставить её значило бы показать
 * преимущество там, где его никто не измерял.
 */
export function bookMid(
  bestBid: string | null | undefined,
  bestAsk: string | null | undefined,
): string | null {
  const bid = parse(bestBid);
  const ask = parse(bestAsk);
  if (bid === null || ask === null) return null;
  // Целочисленно и с округлением вниз: середина справочная, в ордер не идёт.
  return format(Math.floor((bid + ask) / 2));
}

/**
 * Преимущество модели над рынком: `модель − середина стакана`.
 *
 * Пусто, когда сравнивать не с чем, и это не то же самое, что ноль: «стакана
 * нет» и «модель согласна с рынком» — разные утверждения, и второе торгуется, а
 * первое нет.
 */
export function edgeLabel(fair: string | null | undefined, mid: string | null): string {
  const e = edgeMicro(fair, mid);
  if (e === null) return "";
  const sign = e > 0 ? "+" : e < 0 ? "−" : "";
  return `${sign}${roundToFixed(Math.abs(e), 3)}`;
}

/**
 * Тон преимущества: плюс — модель дороже рынка, то есть исход недооценён.
 *
 * Ноль отдельно от «нет данных» намеренно: приглушённый ноль читается как
 * «сошлось», а пустое место — как «не считали».
 */
export function edgeTone(
  fair: string | null | undefined,
  mid: string | null,
): "up" | "down" | "flat" {
  const e = edgeMicro(fair, mid);
  if (e === null || e === 0) return "flat";
  return e > 0 ? "up" : "down";
}

function edgeMicro(fair: string | null | undefined, mid: string | null): number | null {
  const f = parse(fair);
  const m = parse(mid);
  if (f === null || m === null) return null;
  return f - m;
}

/**
 * Кромка ТЕЙКЕРА: `модель − аск − комиссия`, то есть то, что реально достанется.
 *
 * # Зачем рядом с edgeLabel
 *
 * Показанное в строке преимущество считается против СЕРЕДИНЫ стакана, а тейкер
 * платит аск и сверху комиссию. Разница — половина спреда плюс ставка, и на
 * пятиминутках она бывает больше самой кромки: расхождение в десять центов
 * против середины превращается в три против цены налива. Число едет в подсказку,
 * а не в строку, чтобы не двигать вёрстку, — но существовать оно обязано, иначе
 * оператор читает как прибыль то, чего не получит.
 *
 * null — сравнивать не с чем: аска нет (победителя перестают продавать) либо
 * расписание комиссии незнакомо. Ноль вместо прочерка объявил бы вход
 * бесплатным там, где про цену ничего не известно.
 */
export function takerEdgeMicro(
  fair: string | null | undefined,
  bestAsk: string | null | undefined,
  schedule: FeeSchedule | undefined,
): Micro | null {
  const f = parse(fair);
  const ask = parse(bestAsk);
  if (f === null || ask === null || ask <= 0 || ask >= SCALE) return null;
  const fee = feePerShare(schedule, ask);
  if (fee === null) return null;
  return f - ask - fee;
}

/** То же со знаком и тремя знаками после запятой; пусто — считать нечем. */
export function takerEdgeLabel(
  fair: string | null | undefined,
  bestAsk: string | null | undefined,
  schedule: FeeSchedule | undefined,
): string {
  const e = takerEdgeMicro(fair, bestAsk, schedule);
  if (e === null) return "";
  const sign = e > 0 ? "+" : e < 0 ? "−" : "";
  return `${sign}${roundToFixed(Math.abs(e), 3)}`;
}

/**
 * Направление отклонения индекса от страйка — СТРЕЛКОЙ.
 *
 * Знак читается раньше цифр, и в стакане направление обозначено тем же
 * символом: два разных значка для одного «вверх» заставляли бы переучиваться.
 * Ровное совпадение со страйком — не стрелка: там направления нет вовсе.
 */
export function deviationArrow(deviation: string | null | undefined): string {
  const m = parse(deviation);
  if (m === null) return "";
  if (m === 0) return "=";
  return m > 0 ? "▲" : "▼";
}

/** Словами — то, что стрелка говорит значком. Едет в подсказку. */
export function deviationTitle(deviation: string | null | undefined): string {
  const m = parse(deviation);
  if (m === null) return "";
  if (m === 0) return "индекс ровно на страйке";
  return m > 0 ? "индекс ВЫШЕ страйка" : "индекс НИЖЕ страйка";
}

/**
 * С каким тактом опрашивать оценку. `false` — не опрашивать вовсе.
 *
 * # Зачем это здесь
 *
 * Раньше такт был константой в секунду и действовал ВЕЧНО: карточка закрытого
 * окна продолжала стучать в сервер и перерисовывать числа, посчитанные от
 * текущей секунды. Оператор видел «фору 1.1 с» и живой предикшен на окне,
 * которое кончилось час назад.
 *
 * # Почему три состояния, а не два
 *
 * Потому что у закончившегося окна правда доезжает не сразу и двумя частями:
 * итог метрики — через полторы секунды после конца, исход из Gamma — до двух
 * минут (FORECAST_SETTLE). Остановись опрос ровно на границе окна, и сторона
 * резолюции никогда бы на экране не появилась.
 *
 * Поэтому: идущее окно — секунда, как было; закончившееся без подтверждённого
 * исхода — пять секунд, пока правда доезжает; подтверждённый исход — стоп.
 * Дальше меняться нечему: числа приходят из журнала и одинаковы при любом заходе.
 */
export function valuationPollMs(v: Valuation | undefined): number | false {
  // Ответа ещё нет — спрашиваем с обычным тактом: фазу окна назвать нечем.
  if (!v) return 1000;
  if (!v.final) return 1000;
  return v.final.resolved ? false : 5000;
}

/**
 * Показывать ли возраст значения индекса.
 *
 * # Почему это не просто «старше десяти секунд»
 *
 * Потому что у ЗАКОНЧИВШЕГОСЯ окна индекс — это его итог, значение с меткой
 * конца окна. Оно и обязано быть старым, и стареет дальше: через час возраст
 * равен часу. Общее правило зажигало на нём янтарную пометку «значение индекса
 * не обновлялось» — тревогу о поломке там, где число окончательное и верное.
 *
 * Границы окна и так стоят в шапке карточки, а строка итога ярусом ниже прямо
 * говорит, что окно кончилось, — второй раз сообщать это тревожным цветом
 * значит отучать читать тревоги.
 */
export function showsIndexAge(v: Valuation | undefined, now: number): boolean {
  if (!v || v.final) return false;
  // Порог тот же, по которому сервер ставит stale. Возраст показывается и когда
  // сервер уже пометил значение старым, и когда оно состарилось у нас на глазах
  // между ответами.
  const age = indexAge(v, now);
  return v.stale || (Number.isFinite(age) && age > 10_000);
}
